import React, { useState } from 'react';
import { AlertTriangle, RotateCcw, Shield, Trash2, ArrowLeft, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { API_URL } from '../config/api';

/**
 * Reset Usine - Page de réinitialisation complète
 * 
 * Comportement:
 * - CONSERVE le compte utilisateur (email, mot de passe)
 * - SUPPRIME toutes les données financières
 * - RÉINITIALISE le profil utilisateur
 * 
 * Analyse toile d'araignée - Données impactées:
 * - Calendriers → Opérations → Récurrences
 * - Portefeuilles → Soldes
 * - Objectifs d'épargne → Versements
 * - Catégories personnalisées
 * - Profil (prénom, anniversaire)
 */
const ResetPage = () => {
  const [status, setStatus] = useState('idle'); // idle, confirming, resetting, done, error
  const [confirmText, setConfirmText] = useState('');
  const [message, setMessage] = useState('');
  const [deletedCounts, setDeletedCounts] = useState(null);
  const [errorDetails, setErrorDetails] = useState('');

  const BACKEND_URL = API_URL;
  const API = `${BACKEND_URL}/api`;
  
  const CONFIRMATION_WORD = 'SUPPRIMER';
  const isConfirmValid = confirmText.toUpperCase() === CONFIRMATION_WORD;

  const handleStartReset = () => {
    setStatus('confirming');
    setConfirmText('');
  };

  const handleCancel = () => {
    setStatus('idle');
    setConfirmText('');
    setErrorDetails('');
  };

  const doReset = async () => {
    if (!isConfirmValid) return;
    
    setStatus('resetting');
    setMessage('Suppression des données en cours...');

    try {
      // Call API to reset server data
      const response = await fetch(`${API}/user/reset-data`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Erreur serveur (${response.status})`);
      }

      const data = await response.json();
      
      setMessage('Nettoyage local...');

      // Clear local storage (tips, preferences, etc.)
      try { localStorage.clear(); } catch (e) { /* ignore */ }

      // Clear session storage
      try { sessionStorage.clear(); } catch (e) { /* ignore */ }

      setStatus('done');
      setMessage('Reset Usine terminé avec succès !');
      setDeletedCounts(data.deleted_counts || null);

      // Redirect to dashboard after delay (user will see setup wizard)
      setTimeout(() => {
        window.location.replace('/dashboard');
      }, 3000);

    } catch (error) {
      console.error('Reset error:', error);
      setStatus('error');
      setMessage('Erreur lors du reset');
      setErrorDetails(error.message || 'Une erreur inattendue s\'est produite');
    }
  };

  const goBack = () => {
    window.location.href = '/dashboard';
  };

  // Liste des données qui seront supprimées
  const dataToDelete = [
    { icon: '📅', label: 'Calendriers', desc: 'Tous vos calendriers personnels et partagés' },
    { icon: '💳', label: 'Opérations', desc: 'Historique complet des transactions' },
    { icon: '🔄', label: 'Récurrences', desc: 'Opérations automatiques programmées' },
    { icon: '💰', label: 'Portefeuilles', desc: 'Cagnottes et épargnes dédiées' },
    { icon: '🎯', label: 'Objectifs', desc: 'Objectifs d\'épargne et progression' },
    { icon: '👤', label: 'Profil', desc: 'Prénom, anniversaire, préférences' },
  ];

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 50%, #fecaca 100%)'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '24px',
        padding: '32px',
        maxWidth: '480px',
        width: '100%',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)',
      }}>
        
        {/* État initial - Avertissement */}
        {status === 'idle' && (
          <>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{
                width: '80px',
                height: '80px',
                background: 'linear-gradient(135deg, #f97316 0%, #dc2626 100%)',
                borderRadius: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px'
              }}>
                <RotateCcw size={40} color="white" />
              </div>
              <h1 style={{ 
                fontSize: '24px', 
                fontWeight: '700', 
                marginBottom: '8px',
                color: '#dc2626'
              }}>
                Reset Usine
              </h1>
              <p style={{ color: '#6b7280', fontSize: '14px' }}>
                Réinitialisation complète de l'application
              </p>
            </div>

            <div style={{
              background: '#fef3c7',
              border: '2px solid #fbbf24',
              borderRadius: '16px',
              padding: '16px',
              marginBottom: '24px'
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <AlertTriangle size={24} color="#d97706" style={{ flexShrink: 0, marginTop: '2px' }} />
                <div>
                  <p style={{ fontWeight: '600', color: '#92400e', marginBottom: '4px' }}>
                    Attention : Action irréversible
                  </p>
                  <p style={{ fontSize: '13px', color: '#a16207', lineHeight: '1.5' }}>
                    Cette action va supprimer <strong>toutes vos données financières</strong>. 
                    Votre compte sera conservé mais vous devrez reconfigurer l'application.
                  </p>
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <p style={{ 
                fontSize: '13px', 
                fontWeight: '600', 
                color: '#374151', 
                marginBottom: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Données qui seront supprimées :
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {dataToDelete.map((item, idx) => (
                  <div key={idx} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '10px 12px',
                    background: '#fef2f2',
                    borderRadius: '10px',
                    border: '1px solid #fecaca'
                  }}>
                    <span style={{ fontSize: '18px' }}>{item.icon}</span>
                    <div style={{ flex: 1 }}>
                      <span style={{ fontWeight: '500', color: '#991b1b', fontSize: '14px' }}>
                        {item.label}
                      </span>
                      <span style={{ color: '#b91c1c', fontSize: '12px', marginLeft: '8px' }}>
                        {item.desc}
                      </span>
                    </div>
                    <Trash2 size={16} color="#dc2626" />
                  </div>
                ))}
              </div>
            </div>

            <div style={{
              background: '#ecfdf5',
              border: '1px solid #10b981',
              borderRadius: '12px',
              padding: '12px 16px',
              marginBottom: '24px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <Shield size={20} color="#059669" />
              <p style={{ fontSize: '13px', color: '#065f46' }}>
                <strong>Votre compte reste actif</strong> (email et mot de passe conservés)
              </p>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={goBack}
                style={{
                  flex: 1,
                  padding: '14px 20px',
                  borderRadius: '12px',
                  border: '2px solid #e5e7eb',
                  background: 'white',
                  color: '#374151',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'all 0.2s'
                }}
                data-testid="reset-cancel-btn"
              >
                <ArrowLeft size={18} />
                Annuler
              </button>
              <button
                onClick={handleStartReset}
                style={{
                  flex: 1,
                  padding: '14px 20px',
                  borderRadius: '12px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                  color: 'white',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'all 0.2s'
                }}
                data-testid="reset-start-btn"
              >
                <RotateCcw size={18} />
                Continuer
              </button>
            </div>
          </>
        )}

        {/* État de confirmation - Saisie obligatoire */}
        {status === 'confirming' && (
          <>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{
                width: '64px',
                height: '64px',
                background: '#fef2f2',
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                border: '2px solid #fecaca'
              }}>
                <AlertTriangle size={32} color="#dc2626" />
              </div>
              <h1 style={{ 
                fontSize: '22px', 
                fontWeight: '700', 
                marginBottom: '8px',
                color: '#1f2937'
              }}>
                Confirmation requise
              </h1>
              <p style={{ color: '#6b7280', fontSize: '14px', lineHeight: '1.5' }}>
                Pour confirmer la suppression de toutes vos données, 
                tapez <strong style={{ color: '#dc2626' }}>SUPPRIMER</strong> ci-dessous
              </p>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontSize: '12px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '8px',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Tapez "SUPPRIMER" pour confirmer
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="SUPPRIMER"
                autoFocus
                style={{
                  width: '100%',
                  padding: '16px',
                  fontSize: '18px',
                  fontWeight: '600',
                  textAlign: 'center',
                  letterSpacing: '0.1em',
                  border: `2px solid ${isConfirmValid ? '#10b981' : '#e5e7eb'}`,
                  borderRadius: '12px',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  color: isConfirmValid ? '#059669' : '#374151'
                }}
                data-testid="reset-confirm-input"
              />
              {confirmText && !isConfirmValid && (
                <p style={{ 
                  color: '#dc2626', 
                  fontSize: '12px', 
                  marginTop: '8px',
                  textAlign: 'center'
                }}>
                  Veuillez taper exactement "SUPPRIMER"
                </p>
              )}
              {isConfirmValid && (
                <p style={{ 
                  color: '#059669', 
                  fontSize: '12px', 
                  marginTop: '8px',
                  textAlign: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px'
                }}>
                  <CheckCircle2 size={14} />
                  Confirmation validée
                </p>
              )}
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handleCancel}
                style={{
                  flex: 1,
                  padding: '14px 20px',
                  borderRadius: '12px',
                  border: '2px solid #e5e7eb',
                  background: 'white',
                  color: '#374151',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                data-testid="reset-back-btn"
              >
                Annuler
              </button>
              <button
                onClick={doReset}
                disabled={!isConfirmValid}
                style={{
                  flex: 1,
                  padding: '14px 20px',
                  borderRadius: '12px',
                  border: 'none',
                  background: isConfirmValid 
                    ? 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'
                    : '#e5e7eb',
                  color: isConfirmValid ? 'white' : '#9ca3af',
                  fontWeight: '600',
                  cursor: isConfirmValid ? 'pointer' : 'not-allowed',
                  transition: 'all 0.2s'
                }}
                data-testid="reset-execute-btn"
              >
                Réinitialiser
              </button>
            </div>
          </>
        )}

        {/* État en cours */}
        {status === 'resetting' && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Loader2 
              size={48} 
              color="#dc2626" 
              style={{ 
                animation: 'spin 1s linear infinite',
                margin: '0 auto 20px'
              }} 
            />
            <h1 style={{ 
              fontSize: '20px', 
              fontWeight: '700', 
              marginBottom: '12px',
              color: '#374151'
            }}>
              Réinitialisation en cours...
            </h1>
            <p style={{ color: '#6b7280', fontSize: '14px' }}>{message}</p>
            <style>{`
              @keyframes spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        )}

        {/* État terminé avec succès */}
        {status === 'done' && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{
              width: '80px',
              height: '80px',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px'
            }}>
              <CheckCircle2 size={40} color="white" />
            </div>
            <h1 style={{ 
              fontSize: '22px', 
              fontWeight: '700', 
              marginBottom: '12px',
              color: '#059669'
            }}>
              Reset Usine terminé !
            </h1>
            <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '20px' }}>
              {message}
            </p>
            
            {deletedCounts && (
              <div style={{
                background: '#f3f4f6',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '20px',
                textAlign: 'left'
              }}>
                <p style={{ 
                  fontSize: '12px', 
                  fontWeight: '600', 
                  color: '#374151', 
                  marginBottom: '8px',
                  textTransform: 'uppercase'
                }}>
                  Données supprimées :
                </p>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(2, 1fr)', 
                  gap: '4px',
                  fontSize: '12px',
                  color: '#6b7280'
                }}>
                  {deletedCounts.calendars > 0 && <span>📅 {deletedCounts.calendars} calendrier(s)</span>}
                  {deletedCounts.operations > 0 && <span>💳 {deletedCounts.operations} opération(s)</span>}
                  {deletedCounts.recurring_operations > 0 && <span>🔄 {deletedCounts.recurring_operations} récurrence(s)</span>}
                  {deletedCounts.portefeuilles > 0 && <span>💰 {deletedCounts.portefeuilles} portefeuille(s)</span>}
                  {deletedCounts.savings_goals > 0 && <span>🎯 {deletedCounts.savings_goals} objectif(s)</span>}
                </div>
              </div>
            )}
            
            <p style={{ color: '#9ca3af', fontSize: '13px' }}>
              Redirection vers la configuration initiale...
            </p>
          </div>
        )}

        {/* État erreur */}
        {status === 'error' && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{
              width: '80px',
              height: '80px',
              background: '#fef2f2',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              border: '2px solid #fecaca'
            }}>
              <XCircle size={40} color="#dc2626" />
            </div>
            <h1 style={{ 
              fontSize: '22px', 
              fontWeight: '700', 
              marginBottom: '12px',
              color: '#dc2626'
            }}>
              Erreur
            </h1>
            <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '8px' }}>
              {message}
            </p>
            {errorDetails && (
              <p style={{ 
                color: '#9ca3af', 
                fontSize: '12px', 
                marginBottom: '24px',
                background: '#f3f4f6',
                padding: '8px 12px',
                borderRadius: '8px'
              }}>
                {errorDetails}
              </p>
            )}
            <button
              onClick={handleCancel}
              style={{
                padding: '14px 32px',
                borderRadius: '12px',
                border: 'none',
                background: '#374151',
                color: 'white',
                fontWeight: '600',
                cursor: 'pointer'
              }}
              data-testid="reset-retry-btn"
            >
              Réessayer
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPage;
